/**
 * Product Adapters
 *
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 *
 * Adapters for combining products and extracting detail views.
 */

import { getProductDownloadUrl } from "@/lib/constants";
import { getStripeToQlm } from "@/services/productIdConfig";
import { isLegacyLicense } from "@/lib/license-matching";
import { License } from "@/types";
import { logger } from "@/lib/logger";
import type {
  ActiveSubscription,
  DeviceDetails,
  LicenseSummary,
  LifetimeProduct,
  NextPaymentProduct,
  Product,
  PurchasedProduct,
  SubscriptionDetails,
  SubscriptionProduct,
} from "./adapter-types";

export const adaptProducts = async (
  subscriptionProducts: SubscriptionProduct[],
  lifetimeProducts: LifetimeProduct[],
): Promise<Product[]> => {
  if (!subscriptionProducts && !lifetimeProducts) {
    throw new Error("At least one product array must be provided");
  }

  const adaptedProducts: Product[] = [];

  if (subscriptionProducts && Array.isArray(subscriptionProducts)) {
    for (const subProduct of subscriptionProducts) {
      if (!subProduct) continue;
      adaptedProducts.push({
        id: subProduct.id,
        productId: subProduct.productId,
        productName: subProduct.productName,
        licenses: subProduct.licenses || [],
        totalLicenses: subProduct.totalLicenses || 0,
        type: "recurring",
        interval: subProduct.interval,
        totalDevices: subProduct.totalDevices || 0,
        availableDevices: subProduct.availableDevices || 0,
        createdAt: subProduct.createdAt,
        // CHANGE_06 (FCR S483): pass through non-active status for display
        displayStatus: (subProduct as Product).displayStatus,
        accessEndDate: (subProduct as Product).accessEndDate,
      });
    }
  }

  if (lifetimeProducts && Array.isArray(lifetimeProducts)) {
    for (const lifetimeProduct of lifetimeProducts) {
      if (!lifetimeProduct) continue;
      adaptedProducts.push({
        id: lifetimeProduct.id,
        productId: lifetimeProduct.productId,
        productName: lifetimeProduct.productName,
        licenses: lifetimeProduct.licenses || [],
        totalLicenses: lifetimeProduct.totalLicenses || 0,
        type: "one_time",
        totalDevices: lifetimeProduct.totalDevices || 0,
        availableDevices: lifetimeProduct.availableDevices || 0,
        createdAt: lifetimeProduct.createdAt,
        // CHANGE_06 (FCR S483): pass through non-active status for display
        displayStatus: (lifetimeProduct as unknown as { displayStatus?: "refunded" | "expired" }).displayStatus,
        accessEndDate: (lifetimeProduct as unknown as { accessEndDate?: string }).accessEndDate,
      });
    }
  }

  logger.info(
    `Adapted ${adaptedProducts.length} total products (${subscriptionProducts?.length || 0} subscriptions, ${lifetimeProducts?.length || 0} lifetime)`,
  );
  return adaptedProducts;
};

export const adaptPurchasedProducts = (
  products: Product[],
): PurchasedProduct[] => {
  if (!products || products.length === 0) {
    throw new Error(
      "Products data is required to adapt to purchased products.",
    );
  }

  return products.map((product) => ({
    productName: product.productName,
    totalLicenses: product.totalLicenses,
    type: product.type === "recurring" ? "subscription" : "lifetime",
  }));
};

export const adaptNextPaymentProducts = (
  products: ActiveSubscription[],
): NextPaymentProduct[] => {
  if (!products || products.length === 0) {
    return [];
  }

  return products
    .filter((product) => product.status !== "canceled") // B1-2 (FCR S476): fully cancelled subscriptions have no upcoming payment
    .filter((product) => !product.cancelAtPeriodEnd) // S-2c: exclude cancelling subscriptions from next-payment widget
    .filter((product) => product.status !== "past_due") // S417: past_due subscriptions have a failed payment -- they already surface
    // via the grace period banner on the billing page. Showing them in the
    // "Next Payment" widget implies an upcoming charge that will succeed, which
    // is misleading. Industry practice (Stripe dashboard, Shopify) excludes
    // past_due from upcoming-payment displays.
    .map((product) => ({
      id: product.id,
      productName: product.productName,
      nextPaymentDate: product.renewalDate,
      amount: product.amount,
      currency: product.currency,
    }));
};

export const adaptLicenseSummary = (
  products: Product[],
  license: License,
): LicenseSummary => {
  if (!license) {
    throw new Error("License data is required to adapt to license summary.");
  }

  let product = products.find((p) => p.productId === license.StripeProductId);

  // Intelligent matching for legacy licenses
  if (!product && !license.StripeProductId) {
    product = products.find((p) =>
      p.licenses?.some((l) => l.ActivationKey === license.ActivationKey),
    );

    if (!product && isLegacyLicense(license)) {
      product = products.find((p) => {
        const expectedQlmId = getStripeToQlm()[p.productId];
        // NF-2 guard: 0 is a placeholder for unified products whose QLM ID is
        // not yet configured. Treat 0 as "no mapping" to avoid false matches.
        return (
          expectedQlmId !== undefined && expectedQlmId > 0 && license.ProductID === expectedQlmId
        );
      });
      if (product) {
        logger.info(
          `[LicenseSummary] Matched legacy license ${license.ActivationKey} to product ${product.productId} by ProductID`,
        );
      }
    }
  }

  if (!product) {
    logger.warn(
      `Product not found for license ${license.ActivationKey}, using fallback`,
    );
    const calculatedNumLicenses = license.MaxActivations ?? license.NumLicenses ?? 1;
    const calculatedAvailable = license.AvailableLicenses ?? Math.max(0, calculatedNumLicenses - (license.ActivationCount ?? 0));
    return {
      productName: "Unknown Product",
      activationKey: license.ActivationKey,
      version: license.MajorVersion,
      numLicenses: calculatedNumLicenses,
      availableLicenses: calculatedAvailable,
      downloadUrl: license.StripeProductId ? getProductDownloadUrl(license.StripeProductId) : "",
      type: "one_time",
      subscriptionExpiryDate: license.SubscriptionExpiryDate || undefined,
    };
  }

  const calculatedNumLicenses = license.MaxActivations ?? license.NumLicenses ?? 1;
  const calculatedAvailable = license.AvailableLicenses ?? Math.max(0, calculatedNumLicenses - (license.ActivationCount ?? 0));

  // Issue 6 (FCR Pass 6): A licence migrated from Annual → Lifetime keeps the
  // catalogue's "recurring" type but has no SubscriptionExpiryDate. Infer the
  // correct runtime type so the suffix shown on the dashboard is "Lifetime"
  // rather than "Annual License" or "Subscription".
  const inferredType: "recurring" | "one_time" =
    product.type === "recurring" && !license.SubscriptionExpiryDate
      ? "one_time"
      : product.type;

  return {
    productName: product.productName,
    activationKey: license.ActivationKey,
    version: license.MajorVersion,
    numLicenses: calculatedNumLicenses,
    availableLicenses: calculatedAvailable,
    downloadUrl: getProductDownloadUrl(product.productId),
    type: inferredType,
    subscriptionExpiryDate: license.SubscriptionExpiryDate || undefined,
  };
};

export const adaptSubscriptionDetails = (
  subscription: ActiveSubscription,
): SubscriptionDetails => {
  if (!subscription) {
    throw new Error(
      "Subscription data is required to adapt to subscription details.",
    );
  }
  return {
    productId: subscription.productId,
    interval: subscription.interval,
    renewalDate: subscription.renewalDate,
    amountDisplay: subscription.amountDisplay,
    startDate: subscription.startDate,
  };
};

export const adaptDeviceTable = (
  devices: License["Devices"],
): DeviceDetails[] => {
  if (!devices || !Array.isArray(devices)) {
    return [];
  }

  return devices.map((device) => {
    const rawName = (device?.ComputerName ?? "").trim();
    return {
      deviceId: device?.ComputerID ?? "",
      deviceName: rawName || "Unknown Device",
      activatedOn: device?.ActivationDate ?? "",
      lastActive: device?.LastAccessedDate ?? "",
      ip: device?.IP_Address ?? "",
      // A1 (FCR S497): layered offline detection — first available signal wins.
      //  1) IsOffline  -- back-end stamp (A2 fix, most reliable)
      //  2) is_offline -- QLM-derived 0/1 flag from OfflineExpiryDate
      //  3) OfflineExpiryDate present -- direct QLM offline expiry field
      //  4) blank ComputerName -- offline activation page never submits a name
      isOffline:
        device?.IsOffline === true ||
        Boolean(device?.is_offline) ||
        device?.OfflineExpiryDate != null ||
        rawName === "",
    };
  });
};
